import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { Trash2, Minus, Plus, ShoppingBag, ArrowRight } from 'lucide-react';
import { Navbar, Footer } from '../components/Layout';
import { CartContext } from '../App';

const CartPage = () => {
  const { cart, removeFromCart, updateQuantity, cartTotal } = useContext(CartContext);
  
  const deliveryCharge = 999;
  const orderTotal = cartTotal + (cart.length > 0 ? deliveryCharge : 0);

  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-white" data-testid="cart-page">
        <Navbar />
        <div className="pt-24 pb-32">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="py-16">
              <ShoppingBag className="w-16 h-16 mx-auto mb-6 text-[#E5E5E5]" />
              <h1 className="font-heading text-2xl font-semibold text-[#1A1A1A] mb-4">Your cart is empty</h1>
              <p className="font-body text-[#666666] mb-8">Looks like you haven't added any items to your cart yet.</p>
              <Link to="/products" className="btn-primary inline-flex items-center" data-testid="continue-shopping-btn">
                Continue Shopping
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="cart-page">
      <Navbar />
      
      {/* Header */}
      <section className="pt-24 pb-8 bg-[#F9F9F8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="font-heading text-xs uppercase tracking-[0.2em] text-[#C4A882] mb-3">Shopping</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-[#1A1A1A] tracking-tight">
            Your Cart
          </h1>
        </div>
      </section>

      {/* Cart Content */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Cart Items */}
            <div className="lg:col-span-2">
              <div className="space-y-6">
                {cart.map((item) => (
                  <div 
                    key={item.id} 
                    className="flex flex-col sm:flex-row gap-6 p-6 bg-[#F9F9F8]"
                    data-testid={`cart-item-${item.id}`}
                  >
                    {/* Image */}
                    <Link to={`/products/${item.id}`} className="w-full sm:w-32 h-32 flex-shrink-0">
                      <img 
                        src={(item.image_urls && item.image_urls.length > 0) ? item.image_urls[0] : (item.image_url || 'https://via.placeholder.com/400x300?text=in-dekor')} 
                        alt={item.name}
                        className="w-full h-full object-cover"
                      />
                    </Link>
                    
                    {/* Details */}
                    <div className="flex-1">
                      <div className="flex justify-between items-start">
                        <div>
                          <Link to={`/products/${item.id}`}>
                            <h3 className="font-heading text-lg font-medium text-[#1A1A1A] hover:text-[#C4A882] transition-colors">
                              {item.name}
                            </h3>
                          </Link>
                          <p className="font-body text-xs uppercase tracking-[0.15em] text-[#666666] mt-1">
                            {item.category}
                          </p>
                        </div>
                        <button 
                          onClick={() => removeFromCart(item.id)}
                          className="p-2 hover:text-red-500 transition-colors"
                          data-testid={`remove-item-${item.id}`}
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                      
                      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end mt-4">
                        {/* Quantity */}
                        <div className="flex items-center border border-[#E5E5E5] bg-white">
                          <button 
                            onClick={() => updateQuantity(item.id, item.quantity - 1)}
                            className="px-3 py-2 hover:bg-[#F9F9F8] transition-colors"
                            data-testid={`decrease-qty-${item.id}`}
                          >
                            <Minus className="w-4 h-4" />
                          </button>
                          <span className="px-4 py-2 font-body text-sm">{item.quantity}</span>
                          <button 
                            onClick={() => updateQuantity(item.id, item.quantity + 1)}
                            className="px-3 py-2 hover:bg-[#F9F9F8] transition-colors"
                            data-testid={`increase-qty-${item.id}`}
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                        
                        {/* Price */}
                        <p className="font-heading text-lg font-semibold text-[#1A1A1A] mt-4 sm:mt-0">
                          ₹{(item.price * item.quantity).toLocaleString('en-IN')}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-[#1A1A1A] p-6 text-white sticky top-24" data-testid="order-summary">
                <h2 className="font-heading text-xl font-semibold mb-6">Order Summary</h2>
                
                <div className="space-y-4 mb-6">
                  <div className="flex justify-between font-body text-sm">
                    <span className="text-gray-400">Subtotal ({cart.reduce((acc, item) => acc + item.quantity, 0)} items)</span>
                    <span>₹{cartTotal.toLocaleString('en-IN')}</span>
                  </div>
                  <div className="flex justify-between font-body text-sm">
                    <span className="text-gray-400">Delivery (Mumbai)</span>
                    <span>₹{deliveryCharge.toLocaleString('en-IN')}</span>
                  </div>
                  <div className="border-t border-white/20 pt-4">
                    <div className="flex justify-between font-heading text-lg font-semibold">
                      <span>Total</span>
                      <span data-testid="cart-total">₹{orderTotal.toLocaleString('en-IN')}</span>
                    </div>
                  </div>
                </div>
                
                <Link 
                  to="/checkout" 
                  className="w-full bg-[#C4A882] text-white py-4 font-medium text-center block hover:bg-[#A88C66] transition-colors"
                  data-testid="proceed-to-checkout-btn"
                >
                  Proceed to Checkout
                </Link>
                
                <Link 
                  to="/products" 
                  className="w-full text-center block mt-4 font-body text-sm text-gray-400 hover:text-white transition-colors"
                >
                  Continue Shopping
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CartPage;
